// Post + creator data model. These are the shapes the in-browser
// renderer (lib/site-render.ts) turns into the static site, and that
// the publish flow commits to the public repo.
//
// Kept deliberately small for v1 (text posts). Media (video/audio via
// GitHub Releases) lands in Sprint 4; the feed schema already reserves
// the fields (10_FEED_SCHEMA.md).

export type PostKind = "text" | "video" | "audio" | "image";

export interface CreatorProfile {
  handle: string;
  displayName: string;
  bio: string;
  /** Absolute site URL, no trailing slash, e.g. https://x-bka-public.pages.dev */
  siteUrl: string;
  language: string;       // ISO 639-1
  discoverable: boolean;
  /** Viewer theme id (see lib/themes.ts) + accent colour. */
  theme: string;
  accent: string;
  /** Optional longer about-page markdown; falls back to bio. */
  about?: string;
}

export interface Post {
  slug: string;
  title: string;
  summary: string;
  /** Markdown body. */
  body: string;
  tags: string[];
  kinds: PostKind[];
  /** ISO 8601. */
  publishedAt: string;
  updatedAt: string;
  language?: string;
  /** Link-only post: the title points here instead of a hosted body. */
  external?: string | null;
}

/** Rough word count for a markdown body — feeds the `wordCount` feed
 *  field (helps discovery rank long-form vs short-form). */
export function wordCount(markdown: string): number {
  const text = markdown
    .replace(/```[\s\S]*?```/g, " ")   // drop code fences
    .replace(/[#>*_`~\-]+/g, " ")       // drop common md punctuation
    .trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

/** The absolute URL a post will live at on the creator's site. */
export function postUrl(profile: CreatorProfile, slug: string): string {
  return `${profile.siteUrl}/posts/${slug}`;
}

/** Title → URL slug. Lowercase, hyphenated, trimmed. */
export function slugify(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "post";
}

/** First ~160 chars of prose as a fallback summary when the creator
 *  hasn't written one. */
export function autoSummary(body: string): string {
  const text = body.replace(/[#>*_`~\[\]()!-]/g, " ").replace(/\s+/g, " ").trim();
  return text.length > 160 ? text.slice(0, 157).trimEnd() + "…" : text;
}
